"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { useAuth } from "@/contexts/auth-context"
import { getLanguageFromToken } from "@/lib/jwt-utils"
import { getLanguageFromCulture, toIntlLocale, type Language } from "@/lib/locale"

export type { Language }

interface LanguageContextType {
  language: Language
}

const LanguageContext = createContext<LanguageContextType | null>(null)

/**
 * Language for the whole MFE, resolved in this order:
 *
 *   1. the token's `SecurityContactCultureId` claim ('48' en / '88' fr) — the
 *      operator's own FBO language, and the only authoritative source;
 *   2. the host's `?culture=` iframe parameter (en-CA / fr-CA), which arrives
 *      with the URL and so covers the window before the postMessage token
 *      handshake completes;
 *   3. English.
 *
 * There is deliberately no in-app language toggle: the language belongs to the
 * FBO contact, and is changed on their FBO account.
 *
 * The page is a static export, so the prerendered HTML is always English — the
 * culture parameter is applied on mount, which is as early as the client can
 * know it without a hydration mismatch.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  const [language, setLanguage] = useState<Language>("en")

  // Pre-token: the host's culture parameter.
  useEffect(() => {
    if (typeof window === "undefined") return
    const fromCulture = getLanguageFromCulture(window.location.search)
    if (fromCulture) setLanguage(fromCulture)
  }, [])

  // The token wins as soon as it lands. A cleared token (TOKEN_EXPIRED) is not
  // a reason to flip the UI back to English mid-session, so it is left alone.
  useEffect(() => {
    if (token) setLanguage(getLanguageFromToken(token))
  }, [token])

  // Keep <html lang> honest for screen readers and hyphenation; layout.tsx can
  // only ship the static "en".
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = toIntlLocale(language)
    }
  }, [language])

  return <LanguageContext.Provider value={{ language }}>{children}</LanguageContext.Provider>
}

/**
 * Default for components rendered outside a LanguageProvider — most useful for
 * unit tests mounting a child in isolation. Production paths always go through
 * <LanguageProvider> in app/layout.tsx.
 */
const FALLBACK_LANGUAGE_CONTEXT: LanguageContextType = { language: "en" }

export function useLanguage(): LanguageContextType {
  return useContext(LanguageContext) ?? FALLBACK_LANGUAGE_CONTEXT
}
